if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      static MAX_SCALE = 4;
      static TAP_SCALE = 2.5;

      constructor() {
        super();
        this.content = this.querySelector('[role="document"]');
        this.counter = this.querySelector('[data-viewer-counter]');
        this.slides = [];
        this.index = 0;
        this.pointers = new Map();
        this.gesture = null;
        this.lastTap = null;
        this.resetZoomState();

        this.querySelector('[data-viewer-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
        this.querySelector('[data-viewer-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

        // ModalDialog schliesst Media-Modals bei jedem Mausklick. Im Viewer
        // zoomt ein Klick aufs Bild, deshalb endet das pointerup hier.
        this.querySelectorAll('[data-viewer-stop]').forEach((element) =>
          element.addEventListener('pointerup', (event) => {
            if (this.classList.contains('is-viewer')) event.stopPropagation();
          })
        );
        this.content.addEventListener('pointerup', (event) => {
          if (this.classList.contains('is-viewer')) event.stopPropagation();
        });

        this.content.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.content.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.content.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.content.addEventListener('pointercancel', this.onPointerUp.bind(this));
        this.content.addEventListener('dragstart', (event) => event.preventDefault());
        this.content.addEventListener('wheel', this.onWheel.bind(this), { passive: false });

        this.addEventListener('keydown', (event) => {
          if (!this.classList.contains('is-viewer')) return;
          if (event.key === 'ArrowLeft') this.goTo(this.index - 1);
          if (event.key === 'ArrowRight') this.goTo(this.index + 1);
        });

        window.addEventListener('resize', () => {
          if (this.classList.contains('is-viewer')) this.applyZoom(false);
        });

        // Zurueck-Geste auf dem Handy schliesst den Viewer statt die Seite zu verlassen.
        window.addEventListener('popstate', () => {
          if (!this.historyPushed) return;
          this.historyPushed = false;
          if (this.hasAttribute('open')) this.hide(true);
        });
      }

      show(opener) {
        super.show(opener);

        const openedId = this.openedBy.getAttribute('data-media-id');
        this.slides = this.getSlides();
        const start = this.slides.findIndex((slide) => slide.dataset.mediaId === openedId);

        if (start === -1) {
          // Videos und 3D-Modelle laufen weiter ueber die Standardansicht.
          this.classList.remove('is-viewer');
          this.showActiveMedia();
          return;
        }

        this.classList.add('is-viewer');
        this.content.querySelectorAll('.is-slide').forEach((element) => element.classList.remove('is-slide', 'is-current'));
        this.slides.forEach((slide) => {
          slide.classList.add('is-slide');
          slide.sizes = '100vw';
        });

        this.goTo(start, false);

        if (!this.historyPushed) {
          history.pushState({ productModal: true }, '');
          this.historyPushed = true;
        }
      }

      hide(fromHistory) {
        super.hide();
        this.resetZoomState();
        this.slides.forEach((slide) => (slide.style.transform = ''));
        this.content.style.transform = '';
        this.classList.remove('is-viewer', 'is-zoomed');
        this.pointers.clear();
        this.gesture = null;

        if (this.historyPushed && fromHistory !== true) {
          this.historyPushed = false;
          history.back();
        }
      }

      getSlides() {
        const images = Array.from(this.content.querySelectorAll(':scope > img.product-modal-image[data-media-id]'));
        const gallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
        if (!gallery) return images.filter((image) => !image.classList.contains('hide-image'));

        // Die Galerie ist massgeblich: gleiche Bilder, gleiche Reihenfolge.
        return Array.from(gallery.querySelectorAll('.product__media-item[data-media-id]:not(.hide-image)'))
          .map((item) => item.dataset.mediaId.split('-').pop())
          .map((id) => images.find((image) => image.dataset.mediaId === id))
          .filter(Boolean);
      }

      goTo(index, animate = true) {
        if (!this.slides.length) return;
        const target = Math.max(0, Math.min(this.slides.length - 1, index));

        if (target !== this.index || !animate) {
          this.current()?.classList.remove('is-current');
          if (this.current()) this.current().style.transform = '';
          this.resetZoomState();
          this.classList.remove('is-zoomed');
        }

        this.index = target;
        this.current().classList.add('is-current');
        this.content.classList.toggle('is-animating', animate);
        this.content.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;

        // Nachbarbilder vorladen, damit beim Wischen nichts nachlaedt.
        [this.index - 1, this.index + 1].forEach((i) => {
          if (this.slides[i]) this.slides[i].loading = 'eager';
        });
        this.current().loading = 'eager';

        if (this.counter) this.counter.textContent = `${this.index + 1} / ${this.slides.length}`;
        this.toggleAttribute('data-first', this.index === 0);
        this.toggleAttribute('data-last', this.index === this.slides.length - 1);
      }

      current() {
        return this.slides[this.index];
      }

      resetZoomState() {
        this.zoom = { scale: 1, x: 0, y: 0 };
      }

      // Groesse des sichtbaren Bildes innerhalb der object-fit:contain-Box.
      metrics() {
        const image = this.current();
        const width = image.clientWidth;
        const height = image.clientHeight;
        const ratio = image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : width / height;
        const fitWidth = width / height > ratio ? height * ratio : width;
        const fitHeight = width / height > ratio ? height : width / ratio;
        return { width, height, fitWidth, fitHeight, offsetX: (width - fitWidth) / 2, offsetY: (height - fitHeight) / 2 };
      }

      clampAxis(position, scale, box, fit, offset) {
        const scaled = fit * scale;
        if (scaled <= box) return (box - scaled) / 2 - offset * scale;
        return Math.min(-offset * scale, Math.max(box - (offset + fit) * scale, position));
      }

      applyZoom(animate) {
        const image = this.current();
        if (!image) return;
        const m = this.metrics();
        const { scale } = this.zoom;

        if (scale <= 1) {
          this.resetZoomState();
        } else {
          this.zoom.x = this.clampAxis(this.zoom.x, scale, m.width, m.fitWidth, m.offsetX);
          this.zoom.y = this.clampAxis(this.zoom.y, scale, m.height, m.fitHeight, m.offsetY);
          // Beim ersten Zoom die groesste Bildvariante nachladen.
          if (image.sizes !== '400vw') image.sizes = '400vw';
        }

        image.classList.toggle('is-animating', !!animate);
        image.style.transform =
          this.zoom.scale > 1 ? `translate3d(${this.zoom.x}px, ${this.zoom.y}px, 0) scale(${this.zoom.scale})` : '';
        this.classList.toggle('is-zoomed', this.zoom.scale > 1);
      }

      zoomTo(scale, clientX, clientY, animate = true) {
        const rect = this.current().getBoundingClientRect();
        // rect enthaelt die aktuelle Transformation; px/py beziehen sich auf die Box.
        const px = clientX - (rect.left - this.zoom.x);
        const py = clientY - (rect.top - this.zoom.y);
        // Punkt unter dem Finger bleibt beim Zoomen an derselben Stelle.
        const contentX = (px - this.zoom.x) / this.zoom.scale;
        const contentY = (py - this.zoom.y) / this.zoom.scale;
        this.zoom.scale = Math.max(1, Math.min(ProductModal.MAX_SCALE, scale));
        this.zoom.x = px - contentX * this.zoom.scale;
        this.zoom.y = py - contentY * this.zoom.scale;
        this.applyZoom(animate);
      }

      toggleZoom(clientX, clientY) {
        if (this.zoom.scale > 1) {
          this.resetZoomState();
          this.applyZoom(true);
        } else {
          this.zoomTo(ProductModal.TAP_SCALE, clientX, clientY);
        }
      }

      onPointerDown(event) {
        if (!this.classList.contains('is-viewer') || !event.target.closest('.is-slide')) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        this.content.setPointerCapture(event.pointerId);
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

        if (this.pointers.size === 2) {
          const [a, b] = Array.from(this.pointers.values());
          this.resetSwipe();
          this.gesture = {
            type: 'pinch',
            distance: Math.hypot(a.x - b.x, a.y - b.y),
            scale: this.zoom.scale,
            midX: (a.x + b.x) / 2,
            midY: (a.y + b.y) / 2,
            x: this.zoom.x,
            y: this.zoom.y,
          };
        } else if (this.pointers.size === 1) {
          this.startSingle(event);
        }
      }

      startSingle(event) {
        this.gesture = {
          type: this.zoom.scale > 1 ? 'pan' : 'pending',
          startX: event.clientX,
          startY: event.clientY,
          startTime: performance.now(),
          x: this.zoom.x,
          y: this.zoom.y,
          moved: false,
        };
      }

      onPointerMove(event) {
        if (!this.pointers.has(event.pointerId) || !this.gesture) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        const g = this.gesture;

        if (g.type === 'pinch' && this.pointers.size >= 2) {
          const [a, b] = Array.from(this.pointers.values());
          const scale = Math.max(1, Math.min(ProductModal.MAX_SCALE, (g.scale * Math.hypot(a.x - b.x, a.y - b.y)) / g.distance));
          const rect = this.current().getBoundingClientRect();
          // rect enthaelt die aktuelle Transformation; zurueckrechnen auf die Box.
          const boxLeft = rect.left - this.zoom.x;
          const boxTop = rect.top - this.zoom.y;
          const contentX = (g.midX - boxLeft - g.x) / g.scale;
          const contentY = (g.midY - boxTop - g.y) / g.scale;
          const midX = (a.x + b.x) / 2;
          const midY = (a.y + b.y) / 2;
          this.zoom.scale = scale;
          this.zoom.x = midX - boxLeft - contentX * scale;
          this.zoom.y = midY - boxTop - contentY * scale;
          this.applyZoom(false);
          return;
        }

        const dx = event.clientX - g.startX;
        const dy = event.clientY - g.startY;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) g.moved = true;

        if (g.type === 'pan') {
          this.zoom.x = g.x + dx;
          this.zoom.y = g.y + dy;
          this.applyZoom(false);
          return;
        }

        if (g.type === 'pending' && g.moved) g.type = Math.abs(dx) >= Math.abs(dy) ? 'swipe-x' : 'swipe-y';

        if (g.type === 'swipe-x') {
          // Am Anfang und Ende der Reihe gedaempft nachgeben.
          const edge = (this.index === 0 && dx > 0) || (this.index === this.slides.length - 1 && dx < 0);
          const offset = edge ? dx / 3 : dx;
          this.content.classList.remove('is-animating');
          this.content.style.transform = `translate3d(calc(${-this.index * 100}% + ${offset}px), 0, 0)`;
        } else if (g.type === 'swipe-y') {
          this.content.classList.remove('is-animating');
          this.content.style.transform = `translate3d(${-this.index * 100}%, ${dy}px, 0)`;
          this.style.setProperty('--viewer-fade', Math.max(0.3, 1 - Math.abs(dy) / 400));
        }
      }

      onPointerUp(event) {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.delete(event.pointerId);
        const g = this.gesture;
        if (!g) return;

        if (g.type === 'pinch') {
          if (this.zoom.scale < 1.05) {
            this.resetZoomState();
            this.applyZoom(true);
          }
          // Verbleibender Finger schiebt das gezoomte Bild weiter.
          const remaining = Array.from(this.pointers.values())[0];
          if (remaining) this.startSingle({ clientX: remaining.x, clientY: remaining.y });
          else this.gesture = null;
          return;
        }

        if (this.pointers.size > 0) return;
        this.gesture = null;
        const dx = event.clientX - g.startX;
        const dy = event.clientY - g.startY;
        const elapsed = performance.now() - g.startTime;

        if (g.type === 'swipe-x') {
          const fast = Math.abs(dx) / elapsed > 0.4;
          if ((Math.abs(dx) > this.content.clientWidth * 0.2 || fast) && event.type !== 'pointercancel') {
            this.goTo(this.index + (dx < 0 ? 1 : -1));
          } else {
            this.goTo(this.index);
          }
          return;
        }

        if (g.type === 'swipe-y') {
          this.style.removeProperty('--viewer-fade');
          if (Math.abs(dy) > 120 && event.type !== 'pointercancel') this.hide();
          else this.goTo(this.index);
          return;
        }

        if (!g.moved && event.type !== 'pointercancel') this.onTap(event);
      }

      onTap(event) {
        if (event.pointerType === 'mouse') {
          if (this.zoom.scale === 1 && !this.isOnImage(event.clientX, event.clientY)) {
            this.hide();
            return;
          }
          this.toggleZoom(event.clientX, event.clientY);
          return;
        }

        const now = performance.now();
        const last = this.lastTap;
        if (last && now - last.time < 320 && Math.hypot(event.clientX - last.x, event.clientY - last.y) < 40) {
          this.lastTap = null;
          this.toggleZoom(event.clientX, event.clientY);
        } else {
          this.lastTap = { time: now, x: event.clientX, y: event.clientY };
        }
      }

      isOnImage(clientX, clientY) {
        const rect = this.current().getBoundingClientRect();
        const m = this.metrics();
        const x = clientX - rect.left - m.offsetX;
        const y = clientY - rect.top - m.offsetY;
        return x >= 0 && y >= 0 && x <= m.fitWidth && y <= m.fitHeight;
      }

      onWheel(event) {
        if (!this.classList.contains('is-viewer') || !event.ctrlKey) return;
        // Trackpad-Pinch kommt im Browser als wheel mit ctrlKey an.
        event.preventDefault();
        this.zoomTo(this.zoom.scale * Math.exp(-event.deltaY / 100), event.clientX, event.clientY, false);
      }

      resetSwipe() {
        this.content.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;
        this.style.removeProperty('--viewer-fade');
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
